import {
  ExternalLink,
  FileDown,
  Link2,
  ReceiptText,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Tooltip } from "@/components/ui/Tooltip";
import { formatCurrency } from "@/domain/billing";
import {
  formatDate,
  formatDateRange,
} from "@/domain/dates";
import type { InvoiceListItem } from "@/domain/types";
import { displayStatus } from "@/features/invoices/status";

interface Props {
  invoices: InvoiceListItem[];
  selectedPaidIds: Set<string>;
  onTogglePaid: (id: string) => void;
  onCopyLink: (invoice: InvoiceListItem) => void;
  onDownloadInvoice: (id: string) => void;
  onDownloadReceipt: (id: string) => void;
}

export function InvoiceTable({
  invoices,
  selectedPaidIds,
  onTogglePaid,
  onCopyLink,
  onDownloadInvoice,
  onDownloadReceipt,
}: Props) {
  if (invoices.length === 0) {
    return (
      <EmptyState
        icon={ReceiptText}
        title="No matching invoices"
        description="Create an invoice or change your dashboard filters."
        action={
          <Link
            to="/app/invoices/new"
            className="inline-flex min-h-10 items-center rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white"
          >
            Create invoice
          </Link>
        }
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-soft">
      <div className="hidden lg:block">
        <table className="w-full table-fixed text-left">
          <colgroup>
            <col className="w-[5%]" />
            <col className="w-[14%]" />
            <col className="w-[17%]" />
            <col className="w-[15%]" />
            <col className="w-[12%]" />
            <col className="w-[9%]" />
            <col className="w-[10%]" />
            <col className="w-[18%]" />
          </colgroup>

          <thead className="bg-ink text-xs uppercase tracking-wider text-white">
            <tr>
              <th className="px-3 py-4">
                <span className="sr-only">
                  Select paid invoice
                </span>
              </th>
              <th className="px-3 py-4">Invoice</th>
              <th className="px-3 py-4">Client</th>
              <th className="px-3 py-4">Coverage</th>
              <th className="px-3 py-4">Due</th>
              <th className="px-3 py-4">Status</th>
              <th className="px-3 py-4 text-right">
                Total
              </th>
              <th className="px-4 py-4 text-right">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {invoices.map((invoice) => {
              const status = displayStatus(invoice);

              return (
                <tr
                  key={invoice.id}
                  className="hover:bg-surface-muted/45"
                >
                  <td className="px-3 py-4">
                    <input
                      type="checkbox"
                      aria-label={`Select ${invoice.invoiceNumber}`}
                      checked={selectedPaidIds.has(
                        invoice.id,
                      )}
                      disabled={
                        invoice.status !== "paid"
                      }
                      onChange={() =>
                        onTogglePaid(invoice.id)
                      }
                      className="h-4 w-4 rounded border-border accent-brand-600"
                    />
                  </td>

                  <td className="overflow-hidden px-3 py-4">
                    <Link
                      to={`/app/invoices/${invoice.id}`}
                      className="block truncate font-black text-ink hover:text-brand-700"
                    >
                      {invoice.invoiceNumber}
                    </Link>
                    <p className="mt-1 truncate text-xs text-ink-muted">
                      Issued {formatDate(invoice.issueDate)}
                    </p>
                  </td>

                  <td className="overflow-hidden px-3 py-4">
                    <p className="truncate font-semibold text-ink">
                      {invoice.clientName}
                    </p>
                    <p className="mt-1 truncate text-xs text-ink-muted">
                      {invoice.clientCompany || "—"}
                    </p>
                  </td>

                  <td className="overflow-hidden whitespace-nowrap px-3 py-4 text-sm text-ink-muted">
                    {formatDateRange(
                      invoice.serviceStartDate,
                      invoice.serviceEndDate,
                    )}
                  </td>

                  <td className="overflow-hidden px-3 py-4">
                    <p className="truncate text-sm font-semibold text-ink">
                      {invoice.dueDate
                        ? formatDate(invoice.dueDate)
                        : invoice.dueLabel}
                    </p>
                  </td>

                  <td className="overflow-hidden px-3 py-4">
                    <StatusBadge status={status} />
                  </td>

                  <td className="whitespace-nowrap px-3 py-4 text-right">
                    <p className="font-black text-ink">
                      {formatCurrency(
                        invoice.totals.totalCents,
                        invoice.currency,
                      )}
                    </p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-ink-muted">
                      {invoice.currency}
                    </p>
                  </td>

                  <td className="px-2 py-4">
                    <div className="flex flex-nowrap justify-end gap-0.5">
                      <Tooltip label="Download invoice PDF">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 min-h-8 w-8"
                          onClick={() =>
                            onDownloadInvoice(invoice.id)
                          }
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                      </Tooltip>

                      {invoice.status === "paid" ? (
                        <Tooltip label="Download receipt PDF">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 min-h-8 w-8"
                            onClick={() =>
                              onDownloadReceipt(invoice.id)
                            }
                          >
                            <ReceiptText className="h-4 w-4" />
                          </Button>
                        </Tooltip>
                      ) : null}

                      {invoice.share.enabled &&
                      invoice.share.url ? (
                        <Tooltip label="Copy secure client link">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 min-h-8 w-8"
                            onClick={() =>
                              onCopyLink(invoice)
                            }
                          >
                            <Link2 className="h-4 w-4" />
                          </Button>
                        </Tooltip>
                      ) : null}

                      <Tooltip label="Open invoice details">
                        <Link
                          to={`/app/invoices/${invoice.id}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink-muted hover:bg-surface-muted hover:text-ink"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="divide-y divide-border lg:hidden">
        {invoices.map((invoice) => {
          const status = displayStatus(invoice);

          return (
            <article
              key={invoice.id}
              className="p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link
                    to={`/app/invoices/${invoice.id}`}
                    className="font-black text-ink"
                  >
                    {invoice.invoiceNumber}
                  </Link>
                  <p className="mt-1 text-sm font-semibold text-ink">
                    {invoice.clientName}
                  </p>
                </div>

                <StatusBadge status={status} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-surface-muted/60 p-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wider text-ink-muted">
                    Coverage
                  </p>
                  <p className="mt-1 font-medium text-ink">
                    {formatDateRange(
                      invoice.serviceStartDate,
                      invoice.serviceEndDate,
                    )}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-ink-muted">
                    Total
                  </p>
                  <p className="mt-1 font-black text-ink">
                    {formatCurrency(
                      invoice.totals.totalCents,
                      invoice.currency,
                    )}
                  </p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-ink-muted">
                    {invoice.currency}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/app/invoices/${invoice.id}`}
                  className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-ink"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  View
                </Link>

                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    onDownloadInvoice(invoice.id)
                  }
                >
                  <FileDown className="h-3.5 w-3.5" />
                  PDF
                </Button>

                {invoice.share.enabled &&
                invoice.share.url ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      onCopyLink(invoice)
                    }
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Copy link
                  </Button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
